import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError } from '../api';
import { runsApi, type RunAgentOption } from '../api/runs';
import { TASK_STATUSES, tasksApi, type ProjectRef, type RelationKind, type TaskActivity, type TaskComment, type TaskDetail, type TaskExecution, type TaskMember, type TaskPriority, type TaskRow, type TaskStatus } from '../api/tasks';
import { t } from '../i18n';
import { Link } from '../router';
import { RUN_STATUS_KEY, fmtCost, runStatusClass } from './runs.format';
import { fmtDue, fmtWhen, priorityClass, priorityLabel, statusLabel, toDateInput } from './tasks.format';
import './runs.css';
import './tasks.css';

/** The agent badge next to a name — on the assignee, and on every comment an agent wrote. */
const agentBadge = <span className="badge">{t('task.badge.agent')}</span>;

const RELATION_KINDS: RelationKind[] = ['blocks', 'relates'];

export function TaskPage({ id, canWrite, canStartRun, currentActorId }: { id: string; canWrite: boolean; canStartRun: boolean; currentActorId: string }) {
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [editing, setEditing] = useState(false);
  const [members, setMembers] = useState<TaskMember[]>([]);
  const [busy, setBusy] = useState(false);
  const [agents, setAgents] = useState<RunAgentOption[]>([]);
  const [agentInstanceId, setAgentInstanceId] = useState('');

  const reload = () => {
    setConflict(false);
    setError(null);
    return tasksApi
      .get(id)
      .then((tk) => {
        setTask(tk);
        setNotFound(false);
      })
      .catch((e: Error) => {
        if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else setError(e.message);
      });
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Assignee candidates are the members of the task's business (a task.read endpoint, no team.read needed).
  const businessId = task?.businessId;
  useEffect(() => {
    if (!businessId) return;
    tasksApi.members(businessId).then(setMembers).catch((e: Error) => setError(e.message));
  }, [businessId]);

  // Only an `active` instance of this task's business can take the task; a role without `run.read`
  // simply gets no options and the hint under the select says so.
  useEffect(() => {
    if (!businessId || !canStartRun) return;
    runsApi
      .agents(businessId)
      .then((rows) => setAgents(rows.filter((a) => a.status === 'active')))
      .catch(() => setAgents([]));
  }, [businessId, canStartRun]);

  /** The run behind a `run_*` history line, so the line can link to it and show where it stands now. */
  const runById = useMemo(() => new Map((task?.executions ?? []).map((e) => [e.id, e])), [task]);

  /** Names for history lines come with the task (past assignees included), not from the members list. */
  const actorName = useMemo(() => {
    const map = new Map((task?.actors ?? []).map((a) => [a.id, a.displayName]));
    return (actorId: unknown): string => (typeof actorId === 'string' ? (map.get(actorId) ?? '—') : '—');
  }, [task]);

  /** Every write goes through here: a 409 means someone else changed the task first. */
  const guarded = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await reload();
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setConflict(true);
      else setError(e instanceof Error ? e.message : t('common.error'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (notFound) return <p className="muted">{t('task.notFound')}</p>;
  if (!task) return <p className="muted">{error ?? t('task.loading')}</p>;

  const lastActor = task.activity[0]?.actor?.displayName ?? task.createdBy?.displayName ?? t('task.someone');
  const roots = task.comments.filter((c) => !c.parentId);
  const repliesOf = (rootId: string) => task.comments.filter((c) => c.parentId === rootId);

  return (
    <>
      <div className="muted">
        <Link to="/tasks">{t('tasks.title')}</Link> › {task.business.name} › {task.key}
      </div>
      <div className="row task-head" style={{ justifyContent: 'space-between', margin: 'var(--sp-2) 0 var(--sp-4)' }}>
        <h1 style={{ margin: 0 }}>{task.title}</h1>
        <div className="row">
          <select
            value={task.status}
            disabled={!canWrite || busy || conflict}
            aria-label={t('task.status')}
            style={{ width: 'auto' }}
            onChange={(e) => void guarded(() => tasksApi.setStatus(task.id, task.version, e.target.value as TaskStatus))}
          >
            {TASK_STATUSES.map((s) => (
              <option key={s} value={s}>{statusLabel(s)}</option>
            ))}
          </select>
          {canWrite && !editing && (
            <button type="button" className="btn secondary small" onClick={() => setEditing(true)} disabled={conflict}>{t('task.edit')}</button>
          )}
        </div>
      </div>

      {canStartRun && (
        <div className="task-run">
          <label>
            {t('task.run.assign')}
            <select value={agentInstanceId} onChange={(e) => setAgentInstanceId(e.target.value)} disabled={agents.length === 0 || busy}>
              <option value="">—</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn"
            disabled={!agentInstanceId || busy}
            onClick={() => void guarded(() => tasksApi.startRun(task.id, agentInstanceId))}
          >
            {t('task.run.start')}
          </button>
          {agents.length === 0 && <span className="muted">{t('task.run.noAgents')}</span>}
        </div>
      )}

      {conflict && (
        <div className="conflict" role="alert">
          <span>{t('task.conflict')}</span>
          <button type="button" className="btn secondary small" onClick={() => void reload().then(() => setEditing(false))}>{t('task.reload')}</button>
        </div>
      )}
      {error && <div className="error" style={{ marginBottom: 'var(--sp-3)' }}>{error}</div>}

      {editing ? (
        <EditForm task={task} members={members} busy={busy} onCancel={() => setEditing(false)} onSave={async (input) => (await guarded(() => tasksApi.update(task.id, { version: task.version, ...input }))) && setEditing(false)} />
      ) : (
        <div className="grid2">
          <div className="card stack fields">
            <div className="row"><span className="muted k">{t('task.field.business')}</span><span>{task.business.name}</span></div>
            <div className="row"><span className="muted k">{t('task.field.project')}</span><span>{task.project?.name ?? '—'}</span></div>
            <div className="row"><span className="muted k">{t('task.field.assignee')}</span><span>{task.assignee?.displayName ?? '—'} {task.assignee?.type === 'agent' && agentBadge}</span></div>
            <div className="row"><span className="muted k">{t('task.field.priority')}</span><span className={priorityClass(task.priority)}>{priorityLabel(task.priority)}</span></div>
            <div className="row"><span className="muted k">{t('task.field.due')}</span><span>{fmtDue(task.dueAt, true)}</span></div>
            <div className="row"><span className="muted k">{t('task.field.process')}</span><span className="muted">{task.process?.name ?? '—'}</span></div>
            <div className="row"><span className="muted k">{t('task.field.relations')}</span><Relations task={task} canWrite={canWrite && !conflict} onChange={guarded} /></div>
            <div className="row"><span className="muted k">{t('task.field.version')}</span><span className="muted">{t('task.version.line', { version: task.version, when: fmtWhen(task.updatedAt), who: lastActor })}</span></div>
          </div>
          <div className="card">
            <strong>{t('task.description')}</strong>
            {task.description ? <p className="desc" style={{ marginTop: 'var(--sp-2)' }}>{task.description}</p> : <p className="muted" style={{ marginTop: 'var(--sp-2)' }}>{t('task.description.empty')}</p>}
            <div className="muted">{t('task.plan.hint')}</div>
          </div>
        </div>
      )}

      <h2>{t('task.discussion')}</h2>
      <Discussion
        task={task}
        roots={roots}
        repliesOf={repliesOf}
        canWrite={canWrite && !conflict}
        currentActorId={currentActorId}
        busy={busy}
        onSubmit={(body, parentId) => guarded(() => tasksApi.addComment(task.id, body, parentId))}
        onEdit={(commentId, body) => guarded(() => tasksApi.editComment(commentId, body))}
      />

      <h2>{t('task.history')}</h2>
      {task.activity.length === 0 ? (
        <p className="muted">{t('common.none')}</p>
      ) : (
        <ul className="timeline">
          {task.activity.map((a) => (
            <li key={a.id}>
              <span className="muted">{fmtWhen(a.createdAt)}</span>
              <span>
                {describeActivity(a, actorName, runById)}
                <RunLink activity={a} runById={runById} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/**
 * Every `run_*` history line ends in a link to its run, badged with where that run stands now — the
 * task page is where a run is started, so it is also where its result has to be one click away.
 */
function RunLink({ activity, runById }: { activity: TaskActivity; runById: Map<string, TaskExecution> }) {
  const executionId = activity.action.startsWith('run_') ? (activity.after as { executionId?: unknown } | null)?.executionId : undefined;
  if (typeof executionId !== 'string') return null;
  const run = runById.get(executionId);
  return (
    <>
      {' '}
      <Link to={`/runs/${executionId}`}>{t('task.runs.open')}</Link>
      {/* The badge is the run's status now; on a `run_completed` line the text already says how it ended. */}
      {run && activity.action === 'run_started' && <> <span className={runStatusClass(run.status)}>{t(RUN_STATUS_KEY[run.status])}</span></>}
    </>
  );
}

/** The cost the engine stamped on the activity row, with the currency of the run it belongs to. */
function runCost(after: Record<string, unknown>, runById: Map<string, TaskExecution>): string {
  if (typeof after.cost !== 'string') return '—';
  const run = typeof after.executionId === 'string' ? runById.get(after.executionId) : undefined;
  return `${fmtCost(after.cost)} ${run?.currency ?? ''}`.trim();
}

function describeActivity(a: TaskActivity, actorName: (id: unknown) => string, runById: Map<string, TaskExecution>): string {
  const who = a.actor?.displayName ?? t('task.someone');
  const before = a.before ?? {};
  const after = a.after ?? {};
  const s = (v: unknown) => (typeof v === 'string' && (TASK_STATUSES as readonly string[]).includes(v) ? statusLabel(v as TaskStatus) : '—');
  const p = (v: unknown) => (typeof v === 'string' ? priorityLabel(v as TaskPriority) : '—');
  switch (a.action) {
    case 'created': {
      // Mockup: «створила задачу, призначила Олену, термін 12.09» — the assignee and due date set at creation are part of the line.
      let line = t('task.history.created', { who });
      if (typeof after.assigneeId === 'string') line += t('task.history.created.assignee', { to: actorName(after.assigneeId) });
      if (typeof after.dueAt === 'string') line += t('task.history.created.due', { to: fmtDue(after.dueAt, true) });
      return line;
    }
    case 'status':
      return t('task.history.status', { who, from: s(before.status), to: s(after.status) });
    case 'assignee':
      return t('task.history.assignee', { who, to: actorName(after.assigneeId) });
    case 'due':
      return t('task.history.due', { who, to: typeof after.dueAt === 'string' ? fmtDue(after.dueAt, true) : '—' });
    case 'priority':
      return t('task.history.priority', { who, from: p(before.priority), to: p(after.priority) });
    case 'comment':
      return t('task.history.comment', { who });
    case 'edited': {
      const fields = Object.keys(after)
        .map((f) => (f === 'title' ? t('task.history.field.title') : f === 'description' ? t('task.history.field.description') : f === 'projectId' ? t('task.history.field.projectId') : f))
        .join(', ');
      return t('task.history.edited', { who, fields });
    }
    case 'run_started':
      return t('task.history.runStarted', { agent: typeof after.agentName === 'string' ? after.agentName : who });
    case 'run_completed':
      return t('task.history.runCompleted', { cost: runCost(after, runById) });
    case 'run_failed':
      return t('task.history.runFailed');
    case 'run_cancelled':
      return t('task.history.runCancelled');
    case 'relation':
      return t('task.history.relation', { who });
    case 'relation_removed':
      return t('task.history.relation_removed', { who });
    default:
      return t('task.history.other', { who, action: a.action });
  }
}

type EditInput = { title: string; description: string | null; projectId: string | null; priority: TaskPriority; assigneeId: string | null; dueAt: string | null };

function EditForm({ task, members, busy, onCancel, onSave }: { task: TaskDetail; members: TaskMember[]; busy: boolean; onCancel: () => void; onSave: (input: EditInput) => Promise<unknown> }) {
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? '');
  const [projectId, setProjectId] = useState(task.projectId ?? '');
  const [priority, setPriority] = useState<TaskPriority>(task.priority);
  const [assigneeId, setAssigneeId] = useState(task.assigneeId ?? '');
  const [dueAt, setDueAt] = useState(toDateInput(task.dueAt));
  const [projects, setProjects] = useState<ProjectRef[]>([]);

  useEffect(() => {
    tasksApi.projects(task.businessId).then(setProjects).catch(() => setProjects([]));
  }, [task.businessId]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void onSave({ title: title.trim(), description: description.trim() || null, projectId: projectId || null, priority, assigneeId: assigneeId || null, dueAt: dueAt || null });
  };

  // The current assignee stays selectable even after leaving the business, so an unrelated edit does not silently unassign.
  const options = task.assignee && !members.some((m) => m.actorId === task.assignee!.id) ? [{ actorId: task.assignee.id, displayName: task.assignee.displayName, type: task.assignee.type }, ...members] : members;
  return (
    <form className="form card" onSubmit={submit}>
      <label>
        {t('tasks.form.title')}
        <input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={300} />
      </label>
      <label>
        {t('tasks.form.project')}
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <option value="">{t('tasks.form.none')}</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </label>
      <label>
        {t('tasks.form.description')}
        <textarea rows={5} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <div className="row">
        <label style={{ flex: 1 }}>
          {t('tasks.form.priority')}
          <select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
            {(['low', 'normal', 'high', 'urgent'] as TaskPriority[]).map((p) => (
              <option key={p} value={p}>{priorityLabel(p)}</option>
            ))}
          </select>
        </label>
        <label style={{ flex: 1 }}>
          {t('tasks.form.due')}
          <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
        </label>
      </div>
      <label>
        {t('tasks.form.assignee')}
        <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
          <option value="">{t('tasks.form.none')}</option>
          {options.map((m) => (
            <option key={m.actorId} value={m.actorId}>{m.displayName}</option>
          ))}
          <option disabled>{t('tasks.form.agentsLater')}</option>
        </select>
      </label>
      <div className="row">
        <button className="btn" type="submit" disabled={busy}>{t('common.save')}</button>
        <button className="btn secondary" type="button" onClick={onCancel} disabled={busy}>{t('common.cancel')}</button>
      </div>
    </form>
  );
}

function Relations({ task, canWrite, onChange }: { task: TaskDetail; canWrite: boolean; onChange: (fn: () => Promise<unknown>) => Promise<boolean> }) {
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<RelationKind>('relates');
  const [targetId, setTargetId] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [candidates, setCandidates] = useState<TaskRow[]>([]);

  useEffect(() => {
    const h = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(h);
  }, [search]);

  // The picker searches the business by title (server-side), newest first; it never tries to list every task.
  useEffect(() => {
    if (!adding) return;
    let stale = false;
    tasksApi
      .list({ businessId: task.businessId, status: [...TASK_STATUSES], q: q || undefined, pageSize: 50, sort: 'number', dir: 'desc' })
      .then((r) => {
        if (!stale) setCandidates(r.rows.filter((x) => x.id !== task.id));
      })
      .catch(() => {
        if (!stale) setCandidates([]);
      });
    return () => {
      stale = true;
    };
  }, [adding, q, task.businessId, task.id]);

  const out = task.relations.filter((r) => r.targetTask);
  const inn = task.relatedBy.filter((r) => r.task);
  const parts: Array<{ key: string; label: string; task: { id: string; key: string }; relationId?: string }> = [
    ...out.map((r) => ({ key: r.id, label: t(`task.relation.${r.kind}`), task: r.targetTask!, relationId: r.id })),
    ...inn.map((r) => ({ key: r.id, label: r.kind === 'blocks' ? t('task.relation.blockedBy') : t(`task.relation.${r.kind}`), task: r.task! })),
  ];

  const add = async () => {
    if (!targetId) return;
    if (await onChange(() => tasksApi.addRelation(task.id, { kind, targetTaskId: targetId }))) {
      setAdding(false);
      setTargetId('');
      setSearch('');
    }
  };

  return (
    <span className="stack" style={{ gap: 'var(--sp-2)' }}>
      <span>
        {parts.length === 0 && <span className="muted">—</span>}
        {parts.map((p, i) => (
          <span key={p.key}>
            {i > 0 && ' · '}
            {p.label} <Link to={`/tasks/${p.task.id}`}>{p.task.key}</Link>
            {canWrite && p.relationId && (
              <>
                {' '}
                <button type="button" className="btn secondary small" onClick={() => void onChange(() => tasksApi.removeRelation(p.relationId!))} aria-label={`${t('task.relation.remove')} ${p.task.key}`}>{t('task.relation.remove')}</button>
              </>
            )}
          </span>
        ))}
        {canWrite && !adding && (
          <>
            {' '}
            <button type="button" className="btn secondary small" onClick={() => setAdding(true)} aria-label={t('task.relation.kind')}>{t('task.relation.add')}</button>
          </>
        )}
      </span>
      {adding && (
        <span className="row inline-form">
          <select value={kind} onChange={(e) => setKind(e.target.value as RelationKind)} aria-label={t('task.relation.kind')}>
            {RELATION_KINDS.map((k) => (
              <option key={k} value={k}>{t(`task.relation.${k}`)}</option>
            ))}
          </select>
          <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('task.relation.search')} aria-label={t('task.relation.search')} />
          <select value={targetId} onChange={(e) => setTargetId(e.target.value)} aria-label={t('task.relation.target')}>
            <option value="">{t('task.relation.target')}</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>{c.key} · {c.title}</option>
            ))}
          </select>
          <button type="button" className="btn small" onClick={() => void add()} disabled={!targetId}>{t('common.save')}</button>
          <button type="button" className="btn secondary small" onClick={() => setAdding(false)}>{t('common.cancel')}</button>
        </span>
      )}
    </span>
  );
}

function Discussion({
  task,
  roots,
  repliesOf,
  canWrite,
  currentActorId,
  busy,
  onSubmit,
  onEdit,
}: {
  task: TaskDetail;
  roots: TaskComment[];
  repliesOf: (id: string) => TaskComment[];
  canWrite: boolean;
  currentActorId: string;
  busy: boolean;
  onSubmit: (body: string, parentId: string | null) => Promise<boolean>;
  onEdit: (commentId: string, body: string) => Promise<boolean>;
}) {
  const [body, setBody] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    if (await onSubmit(body.trim(), replyTo)) {
      setBody('');
      setReplyTo(null);
    }
  };

  const replyTarget = replyTo ? task.comments.find((c) => c.id === replyTo) : null;

  const message = (c: TaskComment, reply: boolean) => (
    <div key={c.id} className={reply ? 'msg reply' : 'msg'}>
      <span className="who">
        {c.author.displayName} {isAgentComment(c) && agentBadge} · {fmtWhen(c.createdAt)}
        {c.updatedAt !== c.createdAt && ` · ${t('task.comment.edited')}`}
      </span>
      {editingId === c.id ? (
        <CommentEditor initial={c.body} busy={busy} onCancel={() => setEditingId(null)} onSave={async (text) => (await onEdit(c.id, text)) && setEditingId(null)} />
      ) : (
        <span className="body">{c.body}</span>
      )}
      {canWrite && editingId !== c.id && (
        <span className="actions">
          {!reply && <button type="button" className="btn secondary small" onClick={() => setReplyTo(c.id)}>{t('task.comment.reply')}</button>}
          {c.author.id === currentActorId && <button type="button" className="btn secondary small" onClick={() => setEditingId(c.id)}>{t('task.comment.edit')}</button>}
        </span>
      )}
    </div>
  );

  return (
    <div className="thread">
      {roots.length === 0 && <p className="muted">{t('task.comment.none')}</p>}
      {roots.map((c) => (
        <div key={c.id} className="stack" style={{ gap: 'var(--sp-3)' }}>
          {message(c, false)}
          {repliesOf(c.id).map((r) => message(r, true))}
        </div>
      ))}
      {canWrite && (
        <form className="card stack" onSubmit={send}>
          {replyTarget && (
            <div className="row muted">
              <span>{t('task.comment.replying')}: {replyTarget.author.displayName}</span>
              <button type="button" className="btn secondary small" onClick={() => setReplyTo(null)}>{t('common.cancel')}</button>
            </div>
          )}
          <textarea className="input" rows={3} placeholder={t('task.comment.placeholder')} value={body} onChange={(e) => setBody(e.target.value)} />
          <div className="row">
            <button className="btn" type="submit" disabled={busy || !body.trim()}>{t('task.comment.send')}</button>
          </div>
        </form>
      )}
    </div>
  );
}

/**
 * A comment an agent wrote: the author Actor is of type `agent`, and the office connector stamps
 * `metadata.source = 'agent'` on the row it creates for a run — either one marks it.
 */
function isAgentComment(c: TaskComment): boolean {
  return c.author.type === 'agent' || (c.metadata as { source?: unknown } | null | undefined)?.source === 'agent';
}

/** Inline editor for the author's own comment (PATCH /comments/:id). */
function CommentEditor({ initial, busy, onCancel, onSave }: { initial: string; busy: boolean; onCancel: () => void; onSave: (body: string) => Promise<unknown> }) {
  const [text, setText] = useState(initial);
  return (
    <span className="stack" style={{ gap: 'var(--sp-2)' }}>
      <textarea className="input" rows={3} value={text} onChange={(e) => setText(e.target.value)} aria-label={t('task.comment.edit')} />
      <span className="row">
        <button type="button" className="btn small" disabled={busy || !text.trim() || text.trim() === initial} onClick={() => void onSave(text.trim())}>{t('common.save')}</button>
        <button type="button" className="btn secondary small" disabled={busy} onClick={onCancel}>{t('common.cancel')}</button>
      </span>
    </span>
  );
}
